import { z } from 'zod';

import type { PK } from '../database.schema';
import { baseDatabaseSchema, userSchema } from '../database.schema';

import { EvalWorkflowTypeSchema } from './eval-config.schema';

/**
 * Eval-run schemas — Contract Review Bot evaluation feature.
 *
 * Grouped here so they can be removed together with the rest of the eval UI
 * once the evaluation period wraps up.
 */

export const EvalRunStatusSchema = z.enum(['PENDING', 'RUNNING', 'SUCCEEDED', 'FAILED', 'CANCELLED']);

export const EvalStepStatusSchema = z.enum(['PENDING', 'RUNNING', 'SUCCEEDED', 'FAILED']);

export const EvalFindingSeveritySchema = z.enum(['HIGH', 'MEDIUM', 'LOW', 'INFO']);

export const EvalFindingGradeSchema = z.enum(['TRUE_POSITIVE', 'FALSE_POSITIVE', 'PARTIAL', 'NEEDS_REVIEW']);

/** Per-finding lifecycle state. Absent (or null after a reopen) = open. */
export const EvalFindingResolutionSchema = z.enum(['RESOLVED', 'DISMISSED']);

/** Verdict the AI resolution check returns for one finding. */
export const EvalResolutionVerdictSchema = z.enum(['RESOLVED', 'PARTIALLY_RESOLVED', 'NOT_RESOLVED', 'UNCLEAR']);

/**
 * One free-text note on the review as a whole — the channel for "you missed
 * X" and anything else that belongs to no single finding. Stored inline on
 * the run row for the same reason comments live on the finding: the drawer
 * already reads the run in one call, and threads are short.
 */
export const EvalRunFeedbackSchema = z
  .object({
    text: z.string().min(1),
    /** Same actor shape as a finding comment's author; absent on legacy rows. */
    author: userSchema.partial({ tenant_id: true }).optional(),
    created_at: z.string(),
  })
  .strict();

/**
 * Run header content — one row per eval run, attached to a submission.
 *
 * pk: submissions#<submissionId>, sk: eval_runs#<runId>
 */
export const EvalRunContentSchema = z
  .object({
    submission_id: z.string().uuid(),
    eval_config_id: z.string().uuid(),
    eval_config_version_number: z.number().int().positive(),
    eval_config_name: z.string().optional(),
    workflow_type: EvalWorkflowTypeSchema,
    contract_fingerprint: z.string(),
    model_id: z.string(),
    status: EvalRunStatusSchema,
    started_at: z.string().optional(),
    completed_at: z.string().optional(),
    error_message: z.string().optional(),
    total_steps: z.number().int().nonnegative(),
    completed_steps: z.number().int().nonnegative(),
    findings_count: z.number().int().nonnegative().default(0),
    /**
     * Whole-run prompt-cache traffic (warm-ups + every step), stamped by
     * finalize. Writes ≈ one prefix per cache group means the warm-up did
     * its job; writes ≈ one prefix per step means the steps ran cold.
     */
    cache_read_input_tokens: z.number().int().nonnegative().optional(),
    cache_write_input_tokens: z.number().int().nonnegative().optional(),
    triggered_by: userSchema.partial({ tenant_id: true }).optional(),
    // Review-time baseline for the AI resolution check: the exact serialized
    // contract markdown the review model saw, persisted to S3 by the runner.
    // Absent on runs created before the snapshot step shipped.
    /** S3 key of the persisted `fullMarkdown`. */
    contract_snapshot_key: z.string().optional(),
    /**
     * sha256 (hex) of that markdown — the "has review-relevant content
     * changed" signal. `contract_fingerprint` above cannot serve this role:
     * it hashes the whole contract record, so e.g. adding a comment flips it
     * even though comments never reach the serialized markdown.
     */
    contract_snapshot_hash: z.string().optional(),
    /** Run-level reviewer feedback. Append-only, oldest first. */
    feedback: z.array(EvalRunFeedbackSchema).optional(),
  })
  .strict();

export const DbEvalRunSchema = EvalRunContentSchema.merge(baseDatabaseSchema);

/**
 * Per-step progress — one row per workflow unit (main / section#X / lens#Y).
 *
 * pk: submissions#<submissionId>#eval_runs#<runId>, sk: step#<stepKey>
 */
/**
 * Verdict for one checklist check, as written by the audit steps of the
 * removed LENS_AND_AUDIT workflow (Sept 2026). Nothing produces these any
 * more; the schema stays so old runs' step rows still parse and render.
 */
export const EvalCheckVerdictSchema = z.enum(['PASS', 'FAIL', 'NOT_EVIDENCED', 'NOT_APPLICABLE']);

export const EvalCheckResultSchema = z
  .object({
    check_id: z.string().min(1),
    /**
     * The check's text as audited, denormalised from the config version at run
     * time. Runs must stay readable (and attributable) after the config's
     * check wording is edited, and the UI needs the text to render the
     * coverage register without joining back to the config.
     */
    check_text: z.string().optional(),
    verdict: EvalCheckVerdictSchema,
    note: z.string().optional(),
  })
  .strict();

export const EvalRunStepContentSchema = z
  .object({
    run_id: z.string().uuid(),
    step_key: z.string().min(1),
    step_label: z.string().min(1),
    status: EvalStepStatusSchema,
    started_at: z.string().optional(),
    completed_at: z.string().optional(),
    error_message: z.string().optional(),
    input_tokens: z.number().int().nonnegative().optional(),
    output_tokens: z.number().int().nonnegative().optional(),
    /**
     * Prompt-cache traffic for this step's Bedrock call. `input_tokens`
     * above is the uncached part only; a lens that hit the cache shows a
     * large read count here and a small `input_tokens`.
     */
    cache_read_input_tokens: z.number().int().nonnegative().optional(),
    cache_write_input_tokens: z.number().int().nonnegative().optional(),
    findings_count: z.number().int().nonnegative().default(0),
    /** Per-check verdicts for audit steps (absent on lens steps). */
    check_results: z.array(EvalCheckResultSchema).optional(),
  })
  .strict();

export const DbEvalRunStepSchema = EvalRunStepContentSchema.merge(baseDatabaseSchema);

/**
 * One broker comment on a finding — the feedback channel for the limited
 * preview ("this is wrong because…", "we always accept this wording"). Stored
 * inline on the finding rather than as its own row: threads are short, and the
 * drawer already reads every finding in one call.
 */
export const EvalFindingCommentSchema = z
  .object({
    text: z.string().min(1),
    /** Same actor shape as `graded_by` / `resolved_by`; absent on legacy rows. */
    author: userSchema.partial({ tenant_id: true }).optional(),
    created_at: z.string(),
  })
  .strict();

/**
 * Individual finding emitted by a workflow step.
 *
 * pk: submissions#<submissionId>#eval_runs#<runId>, sk: findings#<findingId>
 *
 * `block_id`/`section_id` optional: either anchors the finding to a specific
 * block, or to a section, or to neither (global finding for the contract).
 */
export const EvalFindingContentSchema = z
  .object({
    run_id: z.string().uuid(),
    step_key: z.string().min(1),
    /** Checklist check this finding was emitted for (audit steps only). */
    check_id: z.string().optional(),
    block_id: z.string().optional(),
    section_id: z.string().optional(),
    severity: EvalFindingSeveritySchema,
    title: z.string().min(1),
    description: z.string().min(1),
    recommendation: z.string().optional(),
    guideline_reference: z.string().optional(),
    guideline_pack_id: z.string().optional(),
    grade: EvalFindingGradeSchema.optional(),
    grade_note: z.string().optional(),
    graded_by: userSchema.partial({ tenant_id: true }).optional(),
    graded_at: z.string().optional(),
    // Cross-lens de-duplication (set by the `consolidate` durable step).
    // A finding judged a duplicate of another carries `superseded_by` =
    // the canonical finding's id; the UI hides superseded findings. The
    // surviving canonical finding carries `reported_by_steps` (the step
    // keys / lenses that independently raised the same issue, including its
    // own) and `superseded_count`, so the UI can show a "flagged by N
    // lenses" consensus badge. All three are written as absolute values
    // (cleared to null when a finding is a singleton) so re-running the
    // consolidation step converges. `.nullable()` because the clear path
    // sets them to null rather than removing the attribute.
    superseded_by: z.string().nullable().optional(),
    reported_by_steps: z.array(z.string()).nullable().optional(),
    superseded_count: z.number().int().nonnegative().nullable().optional(),
    // Resolution lifecycle — the future-primary per-finding state (the
    // `grade*` fields above are eval-period-only SME tooling and go away
    // with the eval UI). Absent = open; the reopen path writes nulls rather
    // than removing the attributes, hence `.nullable()`.
    resolution: EvalFindingResolutionSchema.nullable().optional(),
    resolution_source: z.enum(['USER', 'AI']).nullable().optional(),
    /** Set on the USER path only — AI resolutions carry no user. */
    resolved_by: userSchema.partial({ tenant_id: true }).nullable().optional(),
    resolved_at: z.string().nullable().optional(),
    resolution_note: z.string().nullable().optional(),
    // AI verdict history — written for every finding the resolution check
    // assessed, even when the verdict did not resolve it. Deliberately NOT
    // cleared by a reopen: the verdict is a record of what the check saw.
    ai_resolution_verdict: EvalResolutionVerdictSchema.optional(),
    ai_resolution_rationale: z.string().optional(),
    ai_resolution_check_id: z.string().uuid().optional(),
    ai_resolution_at: z.string().optional(),
    /** Append-only, oldest first. */
    comments: z.array(EvalFindingCommentSchema).optional(),
  })
  .strict();

export const DbEvalFindingSchema = EvalFindingContentSchema.merge(baseDatabaseSchema);

/**
 * AI resolution check — one job row per "have my edits fixed these findings?"
 * request against a SUCCEEDED run.
 *
 * pk: submissions#<submissionId>#eval_runs#<runId>, sk: resolution_check#<checkId>
 */
export const EvalResolutionCheckContentSchema = z
  .object({
    run_id: z.string().uuid(),
    submission_id: z.string().uuid(),
    status: EvalRunStatusSchema,
    /** Copied from the run so the check judges with the model that found the issues. */
    model_id: z.string(),
    /**
     * Hash of the CURRENT contract markdown this check assessed — i.e. the
     * post-edit state, as opposed to the run header's
     * `contract_snapshot_hash` (the review-time baseline). Comparing the two
     * is what tells the trigger whether a given contract state has already
     * been checked.
     */
    contract_snapshot_hash: z.string(),
    started_at: z.string().optional(),
    completed_at: z.string().optional(),
    error_message: z.string().optional(),
    total_findings: z.number().int().nonnegative(),
    assessed_findings: z.number().int().nonnegative(),
    resolved_count: z.number().int().nonnegative(),
    /** Whole-check prompt-cache traffic across its batches, stamped by finalize. */
    cache_read_input_tokens: z.number().int().nonnegative().optional(),
    cache_write_input_tokens: z.number().int().nonnegative().optional(),
    triggered_by: userSchema.partial({ tenant_id: true }).optional(),
  })
  .strict();

export const DbEvalResolutionCheckSchema = EvalResolutionCheckContentSchema.merge(baseDatabaseSchema);

/**
 * Shape returned by the LLM for a single finding — the runner parses and
 * enriches it with `run_id`, `step_key`, ids and metadata before persisting.
 */
export const LlmFindingSchema = z
  .object({
    block_id: z.string().optional(),
    section_id: z.string().optional(),
    severity: EvalFindingSeveritySchema,
    title: z.string().min(1),
    description: z.string().min(1),
    recommendation: z.string().optional(),
    guideline_reference: z.string().optional(),
    /** Set by the audit runner, never by the model directly. */
    check_id: z.string().optional(),
  })
  .passthrough();

export const LlmFindingsResponseSchema = z
  .object({
    findings: z.array(LlmFindingSchema),
  })
  .passthrough();

/**
 * Shape returned by the LLM for one audit call (the `emit_audit_results`
 * tool): a verdict per check, a finding on each FAIL / NOT_EVIDENCED.
 * Audit calls report ONLY against their checks — open discovery is the lens
 * steps' job. An `incidental_findings` channel existed until Aug 2026; its
 * first full eval round contributed zero unique recall and one SME-graded
 * false positive, so it was removed (`.passthrough()` still tolerates it on
 * old stored payloads; nothing reads it).
 */
export const LlmAuditCheckResultSchema = z
  .object({
    check_id: z.string().min(1),
    verdict: EvalCheckVerdictSchema,
    note: z.string().optional(),
    finding: LlmFindingSchema.optional(),
  })
  .passthrough();

export const LlmAuditResponseSchema = z
  .object({
    check_results: z.array(LlmAuditCheckResultSchema),
  })
  .passthrough();

/**
 * Shape returned by the cross-lens consolidation LLM call. The model groups
 * findings that describe the same underlying issue into clusters, choosing
 * one existing finding id as the canonical representative and listing the
 * rest as duplicates. Findings not mentioned are treated as singletons.
 *
 * We deliberately do NOT let the model author a merged finding — it only
 * decides grouping + which existing id to keep — so finding text stays
 * traceable to the lens that produced it and never needs re-validation.
 */
export const FindingClusterSchema = z
  .object({
    canonical_id: z.string().min(1),
    duplicate_ids: z.array(z.string().min(1)),
  })
  .passthrough();

export const FindingClustersResponseSchema = z
  .object({
    clusters: z.array(FindingClusterSchema),
  })
  .passthrough();

/**
 * Shape returned by the AI resolution check (the `emit_resolution_verdicts`
 * tool): one verdict per finding in the batch. `finding_id` echoes back the id
 * we sent, so the caller can map verdicts onto rows and detect omissions —
 * the model never invents ids.
 */
export const LlmResolutionVerdictSchema = z
  .object({
    finding_id: z.string().min(1),
    verdict: EvalResolutionVerdictSchema,
    rationale: z.string(),
  })
  .passthrough();

export const LlmResolutionResponseSchema = z
  .object({
    verdicts: z.array(LlmResolutionVerdictSchema),
  })
  .passthrough();

export type EvalRunStatus = z.infer<typeof EvalRunStatusSchema>;
export type EvalStepStatus = z.infer<typeof EvalStepStatusSchema>;
export type EvalFindingSeverity = z.infer<typeof EvalFindingSeveritySchema>;
export type EvalFindingGrade = z.infer<typeof EvalFindingGradeSchema>;
export type EvalFindingResolution = z.infer<typeof EvalFindingResolutionSchema>;
export type EvalResolutionVerdict = z.infer<typeof EvalResolutionVerdictSchema>;
export type EvalRunContent = z.infer<typeof EvalRunContentSchema>;
export type EvalRunFeedback = z.infer<typeof EvalRunFeedbackSchema>;
export type EvalRunStepContent = z.infer<typeof EvalRunStepContentSchema>;
export type EvalFindingComment = z.infer<typeof EvalFindingCommentSchema>;
export type EvalFindingContent = z.infer<typeof EvalFindingContentSchema>;
export type EvalResolutionCheckContent = z.infer<typeof EvalResolutionCheckContentSchema>;
export type DbEvalRun = z.infer<typeof DbEvalRunSchema> & PK;
export type DbEvalRunStep = z.infer<typeof DbEvalRunStepSchema> & PK;
export type DbEvalFinding = z.infer<typeof DbEvalFindingSchema> & PK;
export type DbEvalResolutionCheck = z.infer<typeof DbEvalResolutionCheckSchema> & PK;
export type EvalCheckVerdict = z.infer<typeof EvalCheckVerdictSchema>;
export type EvalCheckResult = z.infer<typeof EvalCheckResultSchema>;
export type LlmFinding = z.infer<typeof LlmFindingSchema>;
export type LlmFindingsResponse = z.infer<typeof LlmFindingsResponseSchema>;
export type LlmAuditCheckResult = z.infer<typeof LlmAuditCheckResultSchema>;
export type LlmAuditResponse = z.infer<typeof LlmAuditResponseSchema>;
export type FindingCluster = z.infer<typeof FindingClusterSchema>;
export type FindingClustersResponse = z.infer<typeof FindingClustersResponseSchema>;
export type LlmResolutionVerdict = z.infer<typeof LlmResolutionVerdictSchema>;
export type LlmResolutionResponse = z.infer<typeof LlmResolutionResponseSchema>;
